/**
 * Registry of active group WebSocket senders, keyed by groupId.
 *
 * `useStudyGroupDoSync` registers a bound `provider.sendMessage`
 * function here so non-React callers (e.g. `sync.studyGroups.send(...)`)
 * can push messages without holding a provider reference themselves.
 *
 * Every call to `enqueueForGroup(groupId, msg)` — the single code path
 * used by `sync.studyGroups.send` — either sends immediately if a
 * sender is registered, or stashes the message in a per-group outbox.
 * When the sync hook registers its sender the outbox is drained
 * immediately, so optimistic mutations made before the socket is up
 * (e.g. a user clicking "Add deck" right after opening the group)
 * fan out as soon as the WebSocket connects. Without this buffer the
 * mutation only caught up on the next `sg-sync-diff` dirty-flush,
 * which is 1–2 seconds later and surprised users as "delayed".
 */

type Sender = (msg: string) => void;
/** Optional notification fired once a queued message actually hits the
 *  wire (synchronous send OR outbox drain). Used by the pending-mutations
 *  tracker to start its timeout clock only after the network attempt. */
type OnSent = () => void;
type Pending = { msg: string; onSent?: OnSent };

const senders = new Map<string, Sender>();
const outbox = new Map<string, Pending[]>();

/** Max messages we'll hold per group before dropping the oldest. */
const OUTBOX_CAP = 200;

function fireOnSent(onSent?: OnSent): void {
  if (!onSent) return;
  try {
    onSent();
  } catch (err) {
    console.error('[sg-sender] onSent callback failed:', err);
  }
}

export function registerGroupProviderSender(groupId: string, sender: Sender): () => void {
  senders.set(groupId, sender);
  // Drain anything queued while we were offline / pre-connect.
  const queued = outbox.get(groupId);
  if (queued && queued.length > 0) {
    outbox.delete(groupId);
    for (const entry of queued) {
      try {
        sender(entry.msg);
        fireOnSent(entry.onSent);
      } catch (err) {
        console.error('[sg-sender] drain failed:', err);
      }
    }
  }
  return () => {
    if (senders.get(groupId) === sender) senders.delete(groupId);
  };
}

export function getGroupProviderSender(groupId: string): Sender | undefined {
  return senders.get(groupId);
}

/**
 * Send a message for a group. If the socket isn't connected yet,
 * the message is buffered and flushed when `registerGroupProviderSender`
 * fires. Callers don't need to branch on connection state.
 *
 * Pass `onSent` to be notified the moment the message actually hits the
 * wire — fired synchronously for the connected path, or later during
 * outbox drain. The pending-mutations tracker uses this to start its
 * timeout only after the first network attempt.
 */
/**
 * The send threw only because the socket has not finished connecting.
 *
 * Browsers report it as `InvalidStateError`; the message wording differs
 * between engines, so the name is what is matched. Any other failure is a real
 * one and must still be reported.
 */
function isNotConnectedYet(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { name?: unknown }).name === 'InvalidStateError'
  );
}

export function enqueueForGroup(groupId: string, msg: string, onSent?: OnSent): void {
  const sender = senders.get(groupId);
  if (sender) {
    try {
      sender(msg);
      fireOnSent(onSent);
      return;
    } catch (err) {
      // The socket not being OPEN yet is the case this function exists to
      // handle: the message falls through to the outbox below and goes out on
      // connect, so nothing is lost and there is nothing for anyone to do.
      // Logging it as an error made a working path look like a failing one —
      // and, because the E2E error-gate reads the console, made every study
      // group flow that sends before the socket settles fail a clean run.
      //
      // Matched by `name`, not `instanceof DOMException`: this module is shared
      // with React Native, where that global does not exist.
      if (!isNotConnectedYet(err)) {
        console.error('[sg-sender] direct send failed:', err);
      }
    }
  }
  const queue = outbox.get(groupId) ?? [];
  queue.push({ msg, onSent });
  if (queue.length > OUTBOX_CAP) queue.shift();
  outbox.set(groupId, queue);
}
